// Version 1 output — the tab shell for a scenario's results. It owns the toggle
// between the three views and the display-currency picker, and composes the
// Staff Plan tab's cards. The FTE matrix itself lives in ModeledMatrixCard,
// which the Summary page also mounts, so the two pages cannot drift apart.

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Coins, LayoutGrid, Sparkles, Users } from 'lucide-react';
import { applyStaffPlanOverrides } from '@rc/engine';
import type {
  CalculationResult,
  ClientProfile,
  Model,
  StaffPlan,
} from '@rc/engine/types';
import HubComparisonCard from './HubComparisonCard';
import CostAnalysisCard from './CostAnalysisCard';
import PhaseConcentrationCard from './PhaseConcentrationCard';
import AutomationImpactView from './AutomationImpactView';
import SummaryView from './SummaryView';
import ModeledMatrixCard from './ModeledMatrixCard';
import type { FindPublishedPlan, PublishPlanArgs } from './publishPlan';
import { useCurrency } from '../../lib/currency';

type ViewMode = 'staff_plan' | 'automation_impact' | 'summary';

interface Props {
  model: Model;
  result: CalculationResult;
  profile: ClientProfile;
  staffPlan?: StaffPlan | null;
  onProfileChange?: (patch: Partial<ClientProfile>) => void;
  /** Publish an output matrix to the client workspace as a saved staff plan.
   *  The caller owns naming provenance and refreshing its own plan list. */
  onPublishPlan?: (args: PublishPlanArgs) => Promise<void>;
  /** Resolves which saved plan a matrix would update in place. */
  findPublishedPlan?: FindPublishedPlan;
  /** Seed for the publish name field, e.g. the scenario's name. */
  defaultPlanName?: string;
}

export default function Version1View({
  model,
  result,
  profile,
  staffPlan,
  onProfileChange,
  onPublishPlan,
  findPublishedPlan,
  defaultPlanName,
}: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('staff_plan');

  // Every card on the Staff Plan tab reads the modeled grid with the scenario's
  // manual cell adjustments applied, so cost, shore mix and phase concentration
  // describe the same team as the FTE matrix being edited between them.
  const adjusted = useMemo(
    () =>
      applyStaffPlanOverrides(
        result.byDisciplineChannelLevel,
        profile.staffPlanOverrides,
        model,
      ),
    [result.byDisciplineChannelLevel, profile.staffPlanOverrides, model],
  );

  if (viewMode === 'automation_impact') {
    return (
      <div className="space-y-4">
        <ViewHeader value={viewMode} onChange={setViewMode} />
        <AutomationImpactView
          model={model}
          result={result}
          profile={profile}
          staffPlan={staffPlan}
          onProfileChange={onProfileChange}
        />
      </div>
    );
  }

  if (viewMode === 'summary') {
    return (
      <div className="space-y-4">
        <ViewHeader value={viewMode} onChange={setViewMode} />
        <SummaryView
          model={model}
          result={result}
          profile={profile}
          staffPlan={staffPlan}
          onProfileChange={onProfileChange}
          onPublishPlan={onPublishPlan}
          findPublishedPlan={findPublishedPlan}
          defaultPlanName={defaultPlanName}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ViewHeader value={viewMode} onChange={setViewMode} />
      <CostAnalysisCard
        model={model}
        cells={adjusted.cells}
        adjusted={adjusted.appliedCount > 0}
        staffPlan={staffPlan}
      />
      <ModeledMatrixCard
        model={model}
        result={result}
        profile={profile}
        onProfileChange={onProfileChange}
        onPublishPlan={onPublishPlan}
        findPublishedPlan={findPublishedPlan}
        defaultPlanName={defaultPlanName}
      />
      <HubComparisonCard
        model={model}
        cells={adjusted.cells}
        adjusted={adjusted.appliedCount > 0}
        staffPlan={staffPlan}
      />
      <PhaseConcentrationCard model={model} cells={adjusted.cells} />
    </div>
  );
}

// Top-of-tab header: the view toggle on the left, and — when the model set up
// more than one currency — a display-currency picker on the right.
function ViewHeader({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <ViewModeToggle value={value} onChange={onChange} />
      <CurrencySelect />
    </div>
  );
}

// Only rendered when the model offers an alternate currency, so a USD-only
// model's output looks exactly as it did before.
function CurrencySelect() {
  const { display, setDisplay, options, base } = useCurrency();
  if (options.length < 2) return null;

  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-500">
      <Coins size={14} className="text-slate-400" />
      <span>Display in</span>
      <select
        className="input py-1 px-2 text-xs w-auto tabular-nums"
        value={display}
        onChange={(e) => setDisplay(e.target.value)}
      >
        {options.map((code) => (
          <option key={code} value={code}>
            {code}
            {code === base ? ' (model)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

// Top-of-tab toggle between the staff-plan analysis cards (cost, FTE table,
// hub comparison, phase concentration) and the automation-impact projection.
function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const options: { id: ViewMode; label: string; icon: React.ReactNode; sub: string }[] = [
    {
      id: 'staff_plan',
      label: 'Staff Plan',
      icon: <Users size={14} />,
      sub: 'Cost, FTE & shore mix vs. modeled plan',
    },
    {
      id: 'automation_impact',
      label: 'Automation Impact',
      icon: <Sparkles size={14} />,
      sub: 'Hours & FTE released by automations over Y1–Y3',
    },
    {
      id: 'summary',
      label: 'Summary',
      icon: <LayoutGrid size={14} />,
      sub: 'Combined hub-shift + automation $ impact across Y1–Y3',
    },
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-1 inline-flex shadow-sm">
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={clsx(
              'flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all',
              active
                ? 'bg-brand-purple-50 text-brand-purple-700 ring-1 ring-brand-purple-200 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50',
            )}
            title={opt.sub}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
